/*
 * [NWF] Accessory finder (NWF-212) — wizard, autocomplete and results of sections/nwf-accessory-finder.liquid.
 *
 * The catalog (manufacturers, models, series) is read from the server rendered link list in the
 * section, which stays the finder without JavaScript. Results are fetched through the Section
 * Rendering API with the storefront filter on nwf.compatible_models (and nwf.compatible_series when
 * the model belongs to a series); both responses are merged by product handle, because Shopify
 * combines different filters with AND and the finder needs OR.
 *
 * On the finder page the selection lives in the URL (?manufacturer=…&series=…|type=…&model=…, results page p and search q) so Back and
 * a shared link work. On a model page (data-model) the page itself is the selection.
 */
(() => {
    const STORAGE_KEY = 'nwf-finder-machine';
    const MAX_PAGES = 10;
    /* ?c=<id> is the chosen accessory category (metaobject id). */
    const categoryParam = (params) => (params.get('c') ? `gid://shopify/Metaobject/${params.get('c')}` : null);

    const storage = {
        get: () => {
            try {
                return window.localStorage.getItem(STORAGE_KEY);
            } catch {
                return null;
            }
        },
        set: (value) => {
            try {
                if (value) window.localStorage.setItem(STORAGE_KEY, value);
                else window.localStorage.removeItem(STORAGE_KEY);
            } catch {
                /* private mode or blocked storage: the switch simply does not persist */
            }
        },
    };

    const text = (root, key, vars = {}) => {
        let value = root.strings[key] || key;
        for (const [name, replacement] of Object.entries(vars)) value = value.replace(`{${name}}`, replacement);
        return value;
    };

    const readCatalog = (root) => {
        const manufacturers = new Map();
        root.querySelectorAll('[data-finder-manufacturer]').forEach((el) => {
            manufacturers.set(el.dataset.handle, {
                handle: el.dataset.handle,
                name: el.dataset.name,
                logo: el.dataset.logo || null,
                models: [],
                series: new Map(),
            });
        });
        const models = [];
        root.querySelectorAll('[data-finder-model]').forEach((el) => {
            const manufacturer = manufacturers.get(el.dataset.manufacturer);
            if (!manufacturer) return;
            const model = {
                handle: el.dataset.handle,
                id: el.dataset.id,
                name: el.dataset.name,
                type: el.dataset.type || '',
                url: el.querySelector('a')?.getAttribute('href') || '',
                manufacturer,
                series: el.dataset.series || null,
                seriesName: el.dataset.seriesName || null,
                seriesId: el.dataset.seriesId || null,
                seriesSort: Number(el.dataset.seriesSort || 9999),
                terms: (el.dataset.terms || '').split('|').filter(Boolean),
            };
            models.push(model);
            manufacturer.models.push(model);
            if (model.series && !manufacturer.series.has(model.series)) {
                manufacturer.series.set(model.series, {
                    handle: model.series,
                    name: model.seriesName,
                    id: model.seriesId,
                    sort: model.seriesSort,
                    models: [],
                });
            }
            if (model.series) manufacturer.series.get(model.series).models.push(model);
        });
        return { manufacturers: [...manufacturers.values()], models };
    };

    /* URL handle for a machine type name: "Nähmaschine" → "naehmaschine". */
    const slugify = (value) =>
        String(value || '')
            .toLowerCase()
            .replace(/ä/g, 'ae')
            .replace(/ö/g, 'oe')
            .replace(/ü/g, 'ue')
            .replace(/ß/g, 'ss')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');

    /* Lower case, no separators, no diacritics: "Nähfuß 7 mm" and "nahfuss7mm" compare equal. */
    const normalise = (value) =>
        String(value || '')
            .toLowerCase()
            .replace(/ß/g, 'ss')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[\s\-_./]+/g, '');

    const el = (tag, attrs = {}, children = []) => {
        const node = document.createElement(tag);
        for (const [key, value] of Object.entries(attrs)) {
            if (value === null || value === undefined || value === false) continue;
            if (key === 'text') node.textContent = value;
            else if (key === 'html') node.innerHTML = value;
            else node.setAttribute(key, value === true ? '' : value);
        }
        for (const child of children) if (child) node.append(child);
        return node;
    };

    /* A grey card the size of a product card, used while the first page loads. */
    const skeletonCard = () =>
        el('div', { class: 'f-column nwf-finder__skeleton', 'aria-hidden': 'true' }, [
            el('div', { class: 'nwf-finder__skeleton-media' }),
            el('div', { class: 'nwf-finder__skeleton-line' }),
            el('div', { class: 'nwf-finder__skeleton-line' }),
            el('div', { class: 'nwf-finder__skeleton-line' }),
        ]);

    /* Category counts across the lists: model plus series minus their intersection. */
    const unionFacets = (pages, crossPage) => {
        const counts = new Map();
        const add = (facets, sign) => {
            for (const facet of facets) {
                const entry = counts.get(facet.value) || { value: facet.value, label: facet.label, count: 0 };
                entry.count += sign * facet.count;
                counts.set(facet.value, entry);
            }
        };
        for (const page of pages) add(page.facets, 1);
        if (crossPage) add(crossPage.facets, -1);
        const total = pages.reduce((sum, p) => sum + p.total, 0) - (crossPage ? crossPage.total : 0);
        const categories = [...counts.values()]
            .filter((c) => c.count > 0)
            .sort((a, b) => a.label.localeCompare(b.label, 'de'));
        return { total, categories };
    };

    class Finder {
        constructor(root) {
            this.root = root;
            this.strings = JSON.parse(root.querySelector('[data-finder-strings]')?.textContent || '{}');
            root.strings = this.strings;
            this.catalog = readCatalog(root);
            this.q = (selector) => root.querySelector(selector);
            this.isModelPage = Boolean(root.dataset.model);
            this.selection = { manufacturer: null, series: null, model: null };
            this.items = [];
            this.chips = { total: 0, categories: [] };
            this.category = null;
            this.requestId = 0;

            this.q('[data-finder-catalog]').hidden = true;
            this.bind();
            this.start();
        }

        /* Liquid paginates the catalog; should it ever span pages, fetch the rest before the wizard starts. */
        async start() {
            const pages = Number(this.q('[data-finder-catalog]')?.dataset.pages || 1);
            const sectionId = this.root.closest('.shopify-section')?.id.replace('shopify-section-', '');
            if (pages > 1 && sectionId) {
                try {
                    for (let page = 2; page <= Math.min(pages, MAX_PAGES); page += 1) {
                        const url = new URL(window.location.pathname, window.location.origin);
                        url.searchParams.set('section_id', sectionId);
                        url.searchParams.set('page', String(page));
                        const doc = new DOMParser().parseFromString(
                            await (await fetch(url.toString())).text(),
                            'text/html',
                        );
                        this.q('[data-finder-models]').append(...doc.querySelectorAll('[data-finder-model]'));
                    }
                    this.catalog = readCatalog(this.root);
                } catch {
                    /* the first page of models is still a working finder */
                }
            }
            if (this.isModelPage) {
                const model = this.catalog.models.find((m) => m.handle === this.root.dataset.model);
                const params = new URLSearchParams(window.location.search);
                const page = Number(params.get('p')) || 1;
                const query = params.get('q') || '';
                if (model) this.selectModel(model, { push: false, page, query, category: categoryParam(params) });
                else this.showEmpty();
                return;
            }
            window.addEventListener('popstate', () => this.applyUrl());
            this.applyUrl(true);
        }

        bind() {
            this.q('[data-finder-back]').addEventListener('click', () => this.back());
            const help = this.q('[data-finder-help]');
            this.q('[data-finder-help-toggle]').addEventListener('click', () => help.showModal());
            this.q('[data-finder-help-close]').addEventListener('click', () => help.close());
            help.addEventListener('click', (event) => {
                if (event.target === help) help.close();
            });
            this.q('[data-finder-remember]').addEventListener('change', (event) => {
                storage.set(event.target.checked && this.selection.model ? this.selection.model.handle : null);
            });
            /* "Maschine ändern" forgets the remembered machine, otherwise the finder would reselect it. */
            this.q('[data-finder-change]').addEventListener('click', (event) => {
                storage.set(null);
                if (this.isModelPage) return;
                event.preventDefault();
                this.reset();
            });
            const query = this.q('[data-finder-query]');
            let timer = null;
            query.addEventListener('input', () => {
                clearTimeout(timer);
                timer = setTimeout(() => {
                    this.query = normalise(query.value);
                    this.renderGrid();
                    this.pushUrl(true);
                    if (this.query) this.ensureAll();
                }, 150);
            });
            this.q('[data-finder-more]').addEventListener('click', async () => {
                const more = this.q('[data-finder-more]');
                more.disabled = true;
                try {
                    await this.loadMore();
                } catch {
                    this.setStatus(text(this.root, 'error'));
                }
                more.disabled = false;
                this.renderGrid();
                this.pushUrl(true);
            });
            this.bindSearch();
        }

        /* ---------- URL and state ---------- */

        /*
         * The middle step narrows a manufacturer's models: by series when the manufacturer has
         * series, otherwise by machine type (the old finder's "realm": sewing, overlock, cutting…).
         * Both are "groups" here; a manufacturer with one type and no series skips the step.
         */
        groupsOf(manufacturer) {
            if (!manufacturer) return [];
            if (manufacturer.series.size) {
                const groups = [...manufacturer.series.values()]
                    .sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name, 'de'))
                    .map((s) => ({ kind: 'series', handle: s.handle, name: s.name, id: s.id, models: s.models }));
                const loose = manufacturer.models.filter((m) => !m.series);
                if (loose.length)
                    groups.push({
                        kind: 'series',
                        handle: '-',
                        name: text(this.root, 'other_models'),
                        id: null,
                        models: loose,
                    });
                return groups;
            }
            const byType = new Map();
            for (const m of manufacturer.models) {
                const key = m.type || '-';
                if (!byType.has(key))
                    byType.set(key, {
                        kind: 'type',
                        handle: slugify(key),
                        name: m.type || text(this.root, 'other_models'),
                        id: null,
                        models: [],
                    });
                byType.get(key).models.push(m);
            }
            const groups = [...byType.values()].sort((a, b) => b.models.length - a.models.length);
            return groups.length > 1 ? groups : [];
        }

        applyUrl(initial = false) {
            const params = new URLSearchParams(window.location.search);
            /* The remembered machine only applies to a plain visit, never to a shared or legacy URL. */
            const explicit = ['model', 'manufacturer', 'series', 'type'].some((key) => params.has(key));
            const modelHandle = params.get('model') || (initial && !explicit ? storage.get() : null);
            const model = modelHandle && this.catalog.models.find((m) => m.handle === modelHandle);
            if (model && model.url) {
                /* A chosen or remembered model lives on its own page; keep page and query. */
                params.delete('model');
                const query = params.toString();
                window.location.replace(`${model.url}${query ? `?${query}` : ''}`);
                return;
            }
            if (model) {
                this.selectModel(model, {
                    push: false,
                    remembered: initial && !params.get('model'),
                    page: Number(params.get('p')) || 1,
                    query: params.get('q') || '',
                    category: categoryParam(params),
                });
                return;
            }
            const manufacturer =
                this.catalog.manufacturers.find((m) => m.handle === params.get('manufacturer')) || null;
            const groupHandle = params.get('series') || params.get('type');
            const group = this.groupsOf(manufacturer).find((g) => g.handle === groupHandle) || null;
            this.selection = { manufacturer, group, model: null };
            this.showWizard();
        }

        pushUrl(replace = false) {
            const params = new URLSearchParams();
            if (this.selection.model) {
                if (!this.isModelPage) params.set('model', this.selection.model.handle);
                if (this.loadedPages > 1) params.set('p', String(this.loadedPages));
                if (this.category) params.set('c', this.category.split('/').pop());
                const query = this.q('[data-finder-query]').value.trim();
                if (query) params.set('q', query);
            } else if (this.isModelPage) return;
            else {
                if (this.selection.manufacturer) params.set('manufacturer', this.selection.manufacturer.handle);
                if (this.selection.group)
                    params.set(this.selection.group.kind === 'series' ? 'series' : 'type', this.selection.group.handle);
            }
            const query = params.toString();
            const url = `${window.location.pathname}${query ? `?${query}` : ''}`;
            try {
                if (replace) window.history.replaceState({}, '', url);
                else window.history.pushState({}, '', url);
            } catch {
                /* history unavailable: the finder still works on the page */
            }
        }

        reset() {
            this.selection = { manufacturer: null, group: null, model: null };
            this.q('[data-finder-remember]').checked = false;
            this.pushUrl();
            this.showWizard();
            this.q('[data-finder-search-input]').focus();
        }

        back() {
            if (this.selection.group) this.selection.group = null;
            else this.selection.manufacturer = null;
            this.pushUrl();
            this.showWizard();
        }

        /* ---------- Wizard ---------- */

        showWizard() {
            this.q('[data-finder-selected]').hidden = true;
            this.q('[data-finder-results]').hidden = true;
            this.q('[data-finder-wizard]').hidden = false;
            const { manufacturer, group } = this.selection;
            const groups = this.groupsOf(manufacturer);
            const total = manufacturer && !groups.length ? 2 : 3;
            const step = !manufacturer ? 1 : groups.length && !group ? 2 : total;

            this.q('[data-finder-back]').hidden = !manufacturer;
            this.q('[data-finder-path]').textContent = [manufacturer?.name, group?.name].filter(Boolean).join(' · ');
            this.q('[data-finder-step-count]').textContent = text(this.root, 'step', { step, total });
            this.root.querySelectorAll('[data-finder-progress]').forEach((bar) => {
                const index = Number(bar.dataset.finderProgress);
                bar.hidden = index > total;
                bar.classList.toggle('is-done', index <= step);
                bar.classList.toggle('is-current', index === step);
            });
            const middle =
                !manufacturer || groups.length
                    ? groups[0]?.kind === 'type'
                        ? 'step_type'
                        : 'step_series'
                    : 'step_model';
            this.q('[data-finder-progress-label]').textContent = text(this.root, middle);

            const tiles = this.q('[data-finder-tiles]');
            tiles.replaceChildren();
            /* Manufacturers are portrait cards (logo above the name), the other steps stay rows. */
            tiles.classList.toggle('nwf-finder__tiles--portrait', !manufacturer);
            const count = (n) => text(this.root, n === 1 ? 'models_one' : 'models_other', { count: n });

            if (!manufacturer) {
                this.q('[data-finder-question]').textContent = text(this.root, 'question_manufacturer');
                for (const m of this.catalog.manufacturers) {
                    tiles.append(
                        this.tile(m.name, count(m.models.length), m.logo || 'placeholder', () => {
                            this.selection.manufacturer = m;
                            this.pushUrl();
                            this.showWizard();
                        }),
                    );
                }
                this.staggerTiles(tiles);
                return;
            }
            if (groups.length && !group) {
                const kind = groups[0].kind;
                this.q('[data-finder-question]').textContent = text(
                    this.root,
                    kind === 'series' ? 'question_series' : 'question_type',
                );
                for (const g of groups) {
                    tiles.append(
                        this.tile(g.name, count(g.models.length), null, () => {
                            this.selection.group = g;
                            this.pushUrl();
                            this.showWizard();
                        }),
                    );
                }
                this.staggerTiles(tiles);
                return;
            }
            this.q('[data-finder-question]').textContent = text(this.root, 'question_model');
            const models = (group ? group.models : manufacturer.models)
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name, 'de', { numeric: true }));
            let letter = null;
            for (const m of models) {
                const initial = /^[0-9]/.test(m.name) ? '0-9' : m.name.charAt(0).toUpperCase();
                if (initial !== letter) {
                    letter = initial;
                    tiles.append(el('h3', { class: 'nwf-finder__letter h6', role: 'presentation', text: letter }));
                }
                tiles.append(
                    this.tile(m.name, group?.kind === 'type' ? null : m.type, false, () => this.openModel(m), m.url),
                );
            }
            this.staggerTiles(tiles);
        }

        /* Entrance delay per tile (CSS animation), capped so long model lists do not trickle in. */
        staggerTiles(tiles) {
            let index = 0;
            for (const child of tiles.children) {
                child.style.setProperty('--nwf-tile-index', String(Math.min(index, 12)));
                index += 1;
            }
        }

        /* logo: an image URL, 'placeholder' for a monogram in place of a missing logo, or nothing. */
        tile(title, subtitle, logo, onSelect, href) {
            let visual = null;
            if (logo === 'placeholder') {
                visual = el('span', {
                    class: 'nwf-finder__tile-logo nwf-finder__tile-logo--placeholder',
                    'aria-hidden': 'true',
                    text: title.charAt(0).toUpperCase(),
                });
            } else if (logo) {
                visual = el('img', { class: 'nwf-finder__tile-logo', src: logo, alt: '', loading: 'lazy' });
            }
            const button = el(
                href ? 'a' : 'button',
                { class: 'nwf-finder__tile', type: href ? null : 'button', href: href || null },
                [
                    visual,
                    el('span', { class: 'nwf-finder__tile-text' }, [
                        el('span', { class: 'nwf-finder__tile-title', text: title }),
                        subtitle ? el('span', { class: 'nwf-finder__tile-sub', text: subtitle }) : null,
                    ]),
                    el('span', { class: 'nwf-finder__tile-arrow', 'aria-hidden': 'true', text: '›' }),
                ],
            );
            button.addEventListener('click', (event) => {
                event.preventDefault();
                onSelect();
            });
            return el('div', { role: 'listitem' }, [button]);
        }

        /* ---------- Search ---------- */

        bindSearch() {
            const input = this.q('[data-finder-search-input]');
            const list = this.q('[data-finder-suggestions]');
            let active = -1;
            let matches = [];

            const close = () => {
                list.hidden = true;
                list.replaceChildren();
                input.setAttribute('aria-expanded', 'false');
                input.removeAttribute('aria-activedescendant');
                active = -1;
            };
            const render = () => {
                list.replaceChildren();
                matches.forEach((model, index) => {
                    const option = el(
                        'li',
                        {
                            id: `${list.id}-${index}`,
                            role: 'option',
                            'aria-selected': index === active ? 'true' : 'false',
                            class: `nwf-finder__suggestion${index === active ? ' is-active' : ''}`,
                        },
                        [
                            el('span', {
                                class: 'nwf-finder__suggestion-name',
                                text: `${model.manufacturer.name} ${model.name}`,
                            }),
                            el('span', {
                                class: 'nwf-finder__suggestion-sub',
                                text: [model.seriesName, model.type].filter(Boolean).join(' · '),
                            }),
                        ],
                    );
                    option.addEventListener('mousedown', (event) => {
                        event.preventDefault();
                        this.openModel(model);
                        input.value = '';
                        close();
                    });
                    list.append(option);
                });
                list.hidden = !matches.length;
                input.setAttribute('aria-expanded', matches.length ? 'true' : 'false');
                if (active >= 0) input.setAttribute('aria-activedescendant', `${list.id}-${active}`);
                else input.removeAttribute('aria-activedescendant');
            };

            input.addEventListener('input', () => {
                const query = normalise(input.value);
                if (query.length < 2) {
                    matches = [];
                    close();
                    return;
                }
                matches = this.search(query);
                active = -1;
                render();
            });
            input.addEventListener('keydown', (event) => {
                if (list.hidden) return;
                if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    active = (active + 1) % matches.length;
                    render();
                } else if (event.key === 'ArrowUp') {
                    event.preventDefault();
                    active = (active - 1 + matches.length) % matches.length;
                    render();
                } else if (event.key === 'Enter' && active >= 0) {
                    event.preventDefault();
                    this.openModel(matches[active]);
                    input.value = '';
                    close();
                } else if (event.key === 'Escape') {
                    close();
                }
            });
            input.addEventListener('blur', close);
        }

        search(query) {
            const scored = [];
            for (const model of this.catalog.models) {
                const full = normalise(`${model.manufacturer.name}${model.name}`);
                const name = normalise(model.name);
                let score = 0;
                if (full.startsWith(query) || name.startsWith(query)) score = 3;
                else if (full.includes(query)) score = 2;
                else if (model.terms.some((term) => normalise(term).includes(query))) score = 1;
                else if (model.seriesName && normalise(`${model.manufacturer.name}${model.seriesName}`).includes(query))
                    score = 1;
                if (score) scored.push({ model, score });
            }
            return scored
                .sort((a, b) => b.score - a.score || a.model.name.length - b.model.name.length)
                .slice(0, 8)
                .map((entry) => entry.model);
        }

        /* ---------- Results ---------- */

        /* Every model has its own page (SEO); the finder only renders results inline as a fallback. */
        openModel(model) {
            if (model.url && !this.isModelPage) window.location.assign(model.url);
            else this.selectModel(model);
        }

        selectModel(model, { push = true, remembered = false, page = 1, query = '', category = null } = {}) {
            const group = this.groupsOf(model.manufacturer).find((g) => g.models.includes(model)) || null;
            this.selection = { manufacturer: model.manufacturer, group, model };
            this.q('[data-finder-wizard]').hidden = true;
            const bar = this.q('[data-finder-selected]');
            bar.hidden = false;
            this.q('[data-finder-selected-name]').textContent = [
                `${model.manufacturer.name} ${model.name}`,
                model.seriesName,
            ]
                .filter(Boolean)
                .join(' · ');
            this.q('[data-finder-remember]').checked = remembered || storage.get() === model.handle;
            /* loadResults sets category, query and the page counter before its first await, so the URL sees the new state. */
            this.loadResults(model, page, query, category);
            if (push) this.pushUrl();
        }

        /*
         * One request per base list (the model, plus its series when it has one). Page 1 is shown
         * at once, "Mehr laden" fetches the next page. Chip counts come from the facet values of the
         * Search & Discovery filter on nwf.accessory_categories in the same responses, so nothing
         * beyond the page on screen is loaded. The two lists overlap (an accessory can point at the
         * model and its series), so a third request with both filters gives the intersection and
         * the union is a + b − ab.
         */
        async loadResults(model, page = 1, query = '', category = null) {
            const results = this.q('[data-finder-results]');
            if (!this.root.dataset.resultsUrl) {
                /* Without the technical collection the filters would be ignored: fail closed. */
                results.hidden = false;
                this.setStatus(text(this.root, 'error'));
                return;
            }
            results.hidden = false;
            this.q('[data-finder-empty]').hidden = true;
            /* A chip click keeps the title, the count and the chips; only the cards reload. */
            const sameModel = this.model === model && this.chips.categories.length > 0;
            const grid = this.q('[data-finder-grid]');
            if (sameModel) {
                /* The old cards stay in place, dimmed, so nothing jumps while the category loads. */
                grid.classList.add('is-loading');
                grid.setAttribute('aria-busy', 'true');
            } else {
                /* Ten placeholders in the grid's own cells: the page keeps its shape while loading. */
                grid.replaceChildren(...Array.from({ length: 10 }, skeletonCard));
                this.q('[data-finder-chips]').replaceChildren();
                this.q('[data-finder-count]').textContent = '';
                this.setStatus(text(this.root, 'loading'), true, true);
            }
            this.q('[data-finder-more-wrap]').hidden = true;
            this.model = model;
            this.category = category;
            this.items = [];
            this.total = 0;
            this.loadedPages = 0;
            this.listPages = [];
            this.query = normalise(query);
            this.q('[data-finder-query]').value = query;
            this.q('[data-finder-no-match]').hidden = true;
            this.q('[data-finder-query]').parentElement.hidden = false;
            const requestId = ++this.requestId;

            const modelFilter = ['filter.p.m.nwf.compatible_models', `gid://shopify/Metaobject/${model.id}`];
            this.lists = [[modelFilter]];
            let cross = null;
            if (model.seriesId) {
                const seriesFilter = ['filter.p.m.nwf.compatible_series', `gid://shopify/Metaobject/${model.seriesId}`];
                this.lists.push([seriesFilter]);
                cross = [modelFilter, seriesFilter];
            }
            try {
                const firsts = await Promise.all(this.lists.map((filters) => this.fetchPage(filters, 1)));
                const crossPage = cross ? await this.fetchPage(cross, 1) : null;
                if (requestId !== this.requestId) return;
                this.absorb(firsts);
                this.total = firsts.reduce((sum, p) => sum + p.total, 0) - (crossPage ? crossPage.total : 0);
                if (category === null) this.chips = unionFacets(firsts, crossPage);
                else if (!sameModel) {
                    /* Deep link with a category: the chips need the counts of the whole model. */
                    const all = await Promise.all(this.lists.map((filters) => this.fetchPage(filters, 1, false)));
                    const allCross = cross ? await this.fetchPage(cross, 1, false) : null;
                    if (requestId !== this.requestId) return;
                    this.chips = unionFacets(all, allCross);
                }
                for (let n = 2; n <= Math.min(page, MAX_PAGES) && this.morePages(); n += 1) {
                    await this.loadMore(requestId);
                    if (requestId !== this.requestId) return;
                }
            } catch {
                if (requestId !== this.requestId) return;
                /* Neither the skeleton nor the previous category's cards may pass as the result. */
                grid.replaceChildren();
                grid.classList.remove('is-loading');
                grid.removeAttribute('aria-busy');
                this.setStatus(text(this.root, 'error'));
                return;
            }
            if (!this.items.length && category === null) {
                this.showEmpty();
                return;
            }
            this.setStatus(
                text(this.root, this.total === 1 ? 'found_one' : 'found_other', { count: this.total }),
                false,
                true,
            );
            this.q('[data-finder-count]').textContent = text(this.root, 'count', { count: this.chips.total });
            this.renderChips();
            this.renderGrid();
            if (this.query) this.ensureAll(requestId);
        }

        /*
         * Fetches the next page of every list that still has one and appends the new cards. The
         * "Mehr laden" button and the text search can ask at the same time, so one request is in
         * flight at most and every caller waits for the same page.
         */
        loadMore(requestId = this.requestId) {
            if (!this.pendingPage) {
                this.pendingPage = this.fetchNextPage(requestId).finally(() => {
                    this.pendingPage = null;
                });
            }
            return this.pendingPage;
        }

        async fetchNextPage(requestId) {
            const next = this.loadedPages + 1;
            const pages = await Promise.all(
                this.lists.map((filters, i) => (this.listPages[i] >= next ? this.fetchPage(filters, next) : null)),
            );
            if (requestId !== this.requestId) return;
            this.absorb(pages.filter(Boolean));
        }

        /* The text search runs over the loaded cards, so a query pulls in the remaining pages first. */
        async ensureAll(requestId = this.requestId) {
            try {
                while (this.morePages() && this.loadedPages < MAX_PAGES) {
                    await this.loadMore(requestId);
                    if (requestId !== this.requestId) return;
                }
            } catch {
                /* the cards already on screen stay searchable */
            }
            if (requestId !== this.requestId) return;
            this.renderGrid();
            this.pushUrl(true);
        }

        absorb(pages) {
            const seen = new Set(this.items.map((item) => item.dataset.handle));
            for (const page of pages) {
                for (const item of page.items) {
                    if (seen.has(item.dataset.handle)) continue;
                    seen.add(item.dataset.handle);
                    this.items.push(item);
                }
            }
            this.loadedPages += 1;
            if (!this.listPages.length) this.listPages = pages.map((page) => page.pages);
        }

        morePages() {
            return this.listPages.some((pages) => pages > this.loadedPages);
        }

        async fetchPage(filters, page, withCategory = true) {
            const url = new URL(this.root.dataset.resultsUrl, window.location.origin);
            for (const [name, value] of filters) url.searchParams.set(name, value);
            if (withCategory && this.category)
                url.searchParams.set('filter.p.m.nwf.accessory_categories', this.category);
            url.searchParams.set('section_id', this.root.dataset.resultsSection);
            if (page > 1) url.searchParams.set('page', String(page));
            const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
            if (!response.ok) throw new Error(`finder: ${response.status}`);
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            const wrapper = doc.querySelector('[data-finder-page]');
            return {
                total: Number(wrapper?.dataset.total) || 0,
                pages: Number(wrapper?.dataset.pages) || 1,
                items: [...doc.querySelectorAll('[data-finder-item]')],
                facets: [...doc.querySelectorAll('[data-finder-facets] li')].map((li) => ({
                    value: li.dataset.value,
                    label: li.dataset.label,
                    count: Number(li.dataset.count) || 0,
                })),
            };
        }

        renderChips() {
            const chips = this.q('[data-finder-chips]');
            const categories = this.chips.categories;
            if (categories.length < 2) {
                chips.replaceChildren();
                return;
            }
            const chip = (label, value, count) => {
                const button = el('button', {
                    type: 'button',
                    class: 'nwf-finder__chip',
                    'aria-pressed': value === this.category ? 'true' : 'false',
                    text: label,
                });
                button.append(el('span', { class: 'nwf-finder__chip-count', text: String(count) }));
                button.addEventListener('click', () => {
                    if (value === this.category) return;
                    chips
                        .querySelectorAll('[aria-pressed]')
                        .forEach((b) => b.setAttribute('aria-pressed', b === button ? 'true' : 'false'));
                    this.loadResults(this.model, 1, this.q('[data-finder-query]').value, value);
                    this.pushUrl(true);
                });
                return button;
            };
            chips.replaceChildren(
                chip(text(this.root, 'all'), null, this.chips.total),
                ...categories.map((c) => chip(c.label, c.value, c.count)),
            );
        }

        renderGrid() {
            const grid = this.q('[data-finder-grid]');
            grid.classList.remove('is-loading');
            grid.removeAttribute('aria-busy');
            const visible = this.items.filter(
                (item) => !this.query || normalise(item.dataset.search).includes(this.query),
            );
            grid.replaceChildren(...visible);
            const noMatch = this.q('[data-finder-no-match]');
            noMatch.hidden = visible.length > 0;
            noMatch.textContent = text(this.root, 'no_match', { query: this.q('[data-finder-query]').value.trim() });
            this.q('[data-finder-more-wrap]').hidden = !this.morePages() || this.loadedPages >= MAX_PAGES;
            this.q('[data-finder-more]').textContent = text(this.root, 'load_more');
            this.q('[data-finder-shown]').textContent = text(this.root, 'shown', {
                shown: visible.length,
                total: this.total,
            });
        }

        showEmpty() {
            this.q('[data-finder-results]').hidden = false;
            this.q('[data-finder-empty]').hidden = false;
            this.q('[data-finder-grid]').replaceChildren();
            this.q('[data-finder-query]').parentElement.hidden = true;
            this.setStatus('');
        }

        /* quiet: announced to assistive tech, not shown (loading has the skeleton, idle has the heading). */
        setStatus(message, loading = false, quiet = false) {
            const status = this.q('[data-finder-status]');
            this.q('[data-finder-status-text]').textContent = message;
            status.classList.toggle('is-loading', loading);
            status.classList.toggle('is-quiet', quiet);
            status.hidden = !message;
        }
    }

    const init = () => {
        document.querySelectorAll('[data-nwf-finder]').forEach((root) => {
            if (root.dataset.nwfFinderReady) return;
            root.dataset.nwfFinderReady = '1';
            try {
                new Finder(root);
            } catch (error) {
                /* The server rendered catalog list is the fallback when the finder cannot start. */
                document.documentElement.classList.remove('nwf-finder-js');
                throw error;
            }
        });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
    document.addEventListener('shopify:section:load', init);
})();
